import dayjs from 'dayjs';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import { AiDataRecognitionResult } from 'constants/documents';
import {
  addIsMappedOnExtractionFieldFlag,
  combineAiModelLayers,
  filterFieldsIntersection,
  flattenExtractionFieldDefinitionTree,
  remapLayoutLayer,
} from 'utils/aiExtractionModels';
import createKeyedSelector from 'utils/createKeyedSelector';
import { checkIsGenAi } from 'utils/documents';

import {
  getDocumentExtractionFieldsWithNamedType,
  selectInspectorRawFieldsByTypeId,
} from 'store/system/selectors';

const getDocumentsState = ({ documents }) => documents;

export const getDocuments = createSelector(getDocumentsState, ({ documents }) => documents);

export const getSelectedDocumentsIds = createSelector(
  getDocumentsState,
  ({ selectedDocumentsIds }) => selectedDocumentsIds,
);

export const getSelectedDocumentsData = createSelector(
  [getDocuments, getSelectedDocumentsIds],
  (documents, selectedDocumentsIds) =>
    selectedDocumentsIds.map(id => {
      const document = documents[id];

      return {
        docTypeName: document?.Type?.name,
        createdDate: dayjs(document?.createdDate).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM),
      };
    }),
);

export const getIsDocumentsSelected = id =>
  createSelector(getSelectedDocumentsIds, selectedDocuments => selectedDocuments.includes(id));

export const getDocumentById = createKeyedSelector(id =>
  createSelector(getDocuments, documents => documents[id]),
);

export const selectIsGenAiDocument = createKeyedSelector(id =>
  createSelector(getDocuments, documents => checkIsGenAi(documents[id])),
);

export const getDocumentExtractionFields = createKeyedSelector(id =>
  createSelector(
    getDocumentById(id),
    document => document?.Type?.extractionSettings?.ExtractionFields ?? [],
  ),
);

export const getAiExtractionModelsGroupedByLayers = createKeyedSelector(id =>
  createSelector(
    [
      getDocumentById(id),
      getDocumentExtractionFields(id),
      getDocumentExtractionFieldsWithNamedType,
      selectInspectorRawFieldsByTypeId,
    ],
    (
      document,
      embeddedExtractionFields,
      sharedEntityMapExtractionFields,
      inspectorFieldsByTypeId,
    ) => {
      if (!document?.aiAnalysisResult) {
        return null;
      }

      // getDocumentExtractionFieldsWithNamedType reads the legacy shared entity map
      // (state.system.documentExtractionFields.fields), populated only by the document-data
      // mapping editor's own fetch — the loan-editor inspector never dispatches it, so it's
      // empty there. Fall back to the document's own embedded field definitions — but most doc
      // types via getDocumentsV2 don't embed them either (document.Type is entirely absent), so
      // fall back once more to the inspector's own fetch (keyed by documentTypeId, which the
      // document itself always carries) so this selector resolves the mapped-fields layer
      // regardless of which UI is asking or how the document loaded.
      const flatEmbeddedExtractionFields =
        flattenExtractionFieldDefinitionTree(embeddedExtractionFields);
      const inspectorFlatFields = inspectorFieldsByTypeId[document.documentTypeId] ?? [];
      const extractionFields =
        [sharedEntityMapExtractionFields, flatEmbeddedExtractionFields, inspectorFlatFields].find(
          candidate => !_.isEmpty(candidate),
        ) ?? [];

      const { extractionResult, layout } = document.aiAnalysisResult;

      const remappedLayout = remapLayoutLayer(layout);

      const filteredLayoutPages = remappedLayout.pages.map(({ fields }) =>
        filterFieldsIntersection(fields),
      );

      const aiModelLayoutCombined = combineAiModelLayers(
        extractionResult.pages,
        filteredLayoutPages,
      );

      const aiModelMappedAiModelFieldsCombined = addIsMappedOnExtractionFieldFlag({
        extractionResult,
        extractionFields,
      });

      const mappedAiModelFields = {
        ...aiModelMappedAiModelFieldsCombined,
        pages: aiModelMappedAiModelFieldsCombined.pages.map(page => ({
          ...page,
          fields: page.fields.filter(
            ({ isMappedOnExtractionField }) => !!isMappedOnExtractionField,
          ),
        })),
      };

      const layoutMappedAiModelFieldsCombined = combineAiModelLayers(
        mappedAiModelFields.pages,
        filteredLayoutPages,
      );

      const allCombinedLayers = combineAiModelLayers(
        aiModelMappedAiModelFieldsCombined.pages,
        filteredLayoutPages,
      );

      return {
        [AiDataRecognitionResult.LAYOUT]: remappedLayout,
        [AiDataRecognitionResult.AI_MODEL]: extractionResult,
        [AiDataRecognitionResult.MAPPED_AI_MODEL_FIELDS]: mappedAiModelFields,
        [AiDataRecognitionResult.LAYOUT_AI_MODEL_COMBINED]: aiModelLayoutCombined,
        [AiDataRecognitionResult.AI_MODEL_MAPPED_AI_MODEL_FIELDS_COMBINED]:
          aiModelMappedAiModelFieldsCombined,
        [AiDataRecognitionResult.LAYOUT_MAPPED_AI_MODEL_FIELDS_COMBINED]:
          layoutMappedAiModelFieldsCombined,
        [AiDataRecognitionResult.ALL_LAYERS]: allCombinedLayers,
      };
    },
  ),
);
